import { Box } from "@mui/material";
import TodaysBrief from "../../Components/TodaysBrief";
import FilterBar from "../../Components/FilterBar";
import FunnelAnalytics from "../../Components/FunnelAnalytics";
import PerformanceMetrics from "../../Components/PerformanceMetrics";
import DealsTable, { buildDealsCsv, triggerCsvDownload } from "../../Components/DealsTable";
import { useEffect, useState, useCallback, useRef } from "react";
import { useGetDealInsightsMutation } from "../../Services/deal-insights";
import { useGetContactInsightsMutation } from "../../Services/contact-insights";
import { useGetDealsMutation } from "../../Services/deal";
import { useGetPipelinesQuery } from "../../Services/pipeline";
import { useGetMeQuery } from "../../Services/user";
import { DealInsights } from "Models/deal-insights";
import {
  DealInsightsResult,
  DealPerformanceMetric,
} from "Models/deal-insights-result";
import { ContactInsights } from "Models/contact-insights";
import {
  ContactInsightsResult,
  ContactPerformanceMetric,
} from "Models/contact-insights-result";
import { DealSummary } from "Models/deal-summary";
import { useAppSelector } from "State/hooks";
import { FunnelStage } from "Models/insights-result";
import { DateType, DateRange, RecordType } from "Models/filter-types";
import { DateTypeToBackend } from "Models/filter-mappers";
import dayjs, { Dayjs } from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";

dayjs.extend(quarterOfYear);

export default function Insights() {
  const [recordType, setRecordType] = useState<RecordType>(RecordType.DEALS);
  const [dateType, setDateType] = useState<DateType>(DateType.CREATE);
  const [dateRange, setDateRange] = useState<DateRange>(DateRange.LAST_MONTH);
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(30, "day")
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [pipelineId, setPipelineId] = useState<string | undefined>(undefined);
  const initialFetchDone = useRef(false);
  const [getDealInsights] = useGetDealInsightsMutation();
  const [getContactInsights] = useGetContactInsightsMutation();
  const [getDeals] = useGetDealsMutation();
  const companyId: string = useAppSelector((state) => state.company.companyId);
  const { data: pipelines } = useGetPipelinesQuery(companyId, { skip: !companyId });
  const { data: userProfile, isSuccess: userProfileLoaded, isError: userProfileError } = useGetMeQuery();
  const [funnelStages, setFunnelStages] = useState<FunnelStage[]>([]);
  const [dealPerformance, setDealPerformance] = useState<
    DealPerformanceMetric[]
  >([]);
  const [contactPerformance, setContactPerformance] = useState<
    ContactPerformanceMetric[]
  >([]);
  const [deals, setDeals] = useState<DealSummary[]>([]);
  const [dealsTotal, setDealsTotal] = useState(0);
  const [dealsPage, setDealsPage] = useState(0);
  const [dealsPageSize, setDealsPageSize] = useState(25);
  const [dealsStages, setDealsStages] = useState<string[]>([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const applyDateRange = (range: DateRange) => {
    const now = dayjs();
    switch (range) {
      case DateRange.TODAY:
        return { start: now.startOf("day"), end: now.endOf("day") };
      case DateRange.YESTERDAY:
        return {
          start: now.subtract(1, "day").startOf("day"),
          end: now.subtract(1, "day").endOf("day"),
        };
      case DateRange.THIS_WEEK:
        return { start: now.startOf("week"), end: now };
      case DateRange.LAST_WEEK:
        return {
          start: now.subtract(1, "week").startOf("week"),
          end: now.subtract(1, "week").endOf("week"),
        };
      case DateRange.LAST_7_DAYS:
        return { start: now.subtract(7, "day"), end: now };
      case DateRange.LAST_28_DAYS:
        return { start: now.subtract(28, "day"), end: now };
      case DateRange.LAST_30_DAYS:
        return { start: now.subtract(30, "day"), end: now };
      case DateRange.LAST_90_DAYS:
        return { start: now.subtract(90, "day"), end: now };
      case DateRange.LAST_180_DAYS:
        return { start: now.subtract(180, "day"), end: now };
      case DateRange.LAST_365_DAYS:
        return { start: now.subtract(365, "day"), end: now };
      case DateRange.THIS_MONTH:
        return { start: now.startOf("month"), end: now };
      case DateRange.LAST_MONTH:
        return {
          start: now.subtract(1, "month").startOf("month"),
          end: now.subtract(1, "month").endOf("month"),
        };
      case DateRange.THIS_QUARTER:
        return { start: now.startOf("quarter"), end: now };
      case DateRange.LAST_QUARTER:
        return {
          start: now.subtract(1, "quarter").startOf("quarter"),
          end: now.subtract(1, "quarter").endOf("quarter"),
        };
      case DateRange.THIS_YEAR:
        return { start: now.startOf("year"), end: now };
      case DateRange.LAST_YEAR:
        return {
          start: now.subtract(1, "year").startOf("year"),
          end: now.subtract(1, "year").endOf("year"),
        };
      case DateRange.ALL_TIME:
        return { start: dayjs("2000-01-01"), end: now };
      case DateRange.CUSTOM:
        // keep current start/end dates
        break;
    }
  };

  const fetchInsights = useCallback(async () => {
    if (!startDate || !endDate) return;
    if (!startDate.isValid() || !endDate.isValid()) return;
    if (startDate.isAfter(endDate, "day")) return;

    try {
      setLoading(true);

      // ✅ CLEARS OLD DATA
      setFunnelStages([]);
      setDealPerformance([]);
      setContactPerformance([]);

      if (recordType === RecordType.DEALS) {
        const request: DealInsights = {
          dateType: DateTypeToBackend[dateType],
          dateRange: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          },
          ...(pipelineId && { pipelineId }),
        };

        const res: DealInsightsResult = await getDealInsights({
          companyId,
          params: request,
        }).unwrap();

        setFunnelStages(res.metrics.dealFunnel.stages ?? []);
        setDealPerformance(res.metrics.performance ?? []);
      } else if (recordType === RecordType.CONTACTS) {
        const request: ContactInsights = {
          dateType: DateTypeToBackend[dateType],
          dateRange: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          },
        };

        const res: ContactInsightsResult = await getContactInsights({
          companyId,
          params: request,
        }).unwrap();

        setFunnelStages(res.metrics.contactFunnel.stages ?? []);
        setContactPerformance(res.metrics.contactVelocity ?? []);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch data");

      // reset state on error
      setFunnelStages([]);
      setDealPerformance([]);
      setContactPerformance([]);
    } finally {
      setLoading(false);
    }
  }, [
    startDate,
    endDate,
    dateType,
    recordType,
    pipelineId,
    companyId,
    getDealInsights,
    getContactInsights,
  ]);

  const fetchDeals = useCallback(
    async (page: number, pageSize: number, stages: string[]) => {
      if (recordType !== RecordType.DEALS) return;
      if (!companyId) return;
      if (!startDate || !endDate) return;
      if (!startDate.isValid() || !endDate.isValid()) return;
      if (startDate.isAfter(endDate, "day")) return;

      const startIso = startDate.toISOString();
      const endIso = endDate.toISOString();
      const filterParams =
        dateType === DateType.CREATE
          ? { createDateFrom: startIso, createDateTo: endIso }
          : { closeDateFrom: startIso, closeDateTo: endIso };

      // Backend filter compares against the resolved pipeline label, not the
      // sourcePipelineId — so send the label.
      const pipelineLabel = pipelineId
        ? pipelines?.find((p) => p.sourcePipelineId === pipelineId)?.label
        : undefined;

      try {
        setDealsLoading(true);
        const res = await getDeals({
          companyId,
          params: {
            paginationParams: { page, pageSize },
            filterParams: {
              ...filterParams,
              ...(pipelineLabel && { pipeline: pipelineLabel }),
              ...(stages.length > 0 && { stages }),
            },
          },
        }).unwrap();

        setDeals(res.data ?? []);
        setDealsTotal(res.count ?? 0);
      } catch (err) {
        console.error(err);
        setDeals([]);
        setDealsTotal(0);
      } finally {
        setDealsLoading(false);
      }
    },
    [recordType, companyId, startDate, endDate, dateType, pipelineId, pipelines, getDeals],
  );

  useEffect(() => {
    const dates = applyDateRange(dateRange);
    if (!dates) return;

    setStartDate((prev) =>
      prev?.isSame(dates.start, "day") ? prev : dates.start,
    );
    setEndDate((prev) => (prev?.isSame(dates.end, "day") ? prev : dates.end));
  }, [dateRange]);

  useEffect(() => {
    if (!pipelines || pipelines.length === 0) return;

    const stageMap: Record<string, string> = {};
    pipelines.forEach((pipeline) => {
      pipeline.stages.forEach((stage) => {
        if (stage.value) stageMap[stage.value] = stage.label;
      });
    });
    localStorage.setItem("stageValueToLabel", JSON.stringify(stageMap));

    if (pipelineId) return;
    if (!userProfileLoaded && !userProfileError) return;
    const defaultPipelineId = userProfile?.defaultPipeline ?? "";
    const preferred = defaultPipelineId
      ? pipelines.find((p) => p.sourcePipelineId === defaultPipelineId)
      : undefined;
    setPipelineId(preferred?.sourcePipelineId ?? pipelines[0].sourcePipelineId);
  }, [pipelines, pipelineId, userProfile, userProfileLoaded, userProfileError]);

  useEffect(() => {
    if (!companyId) return;
    if (recordType === RecordType.DEALS && !pipelineId) return;
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;
    fetchInsights();
    fetchDeals(0, dealsPageSize, dealsStages);
  }, [companyId, pipelineId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stages are pipeline-specific — clear the selected stages when the pipeline changes.
  useEffect(() => {
    setDealsStages([]);
  }, [pipelineId]);

  const onApplyFilters = () => {
    setError(null);

    if (!startDate || !endDate) return;

    if (!startDate.isValid() || !endDate.isValid()) return;

    if (startDate.isAfter(endDate, "day")) {
      setError("Start date cannot be after end date");
      return;
    }

    fetchInsights();
    setDealsPage(0);
    fetchDeals(0, dealsPageSize, dealsStages);
  };

  const onDealsPageChange = (nextPage: number) => {
    setDealsPage(nextPage);
    fetchDeals(nextPage, dealsPageSize, dealsStages);
  };

  const onDealsPageSizeChange = (nextPageSize: number) => {
    setDealsPageSize(nextPageSize);
    setDealsPage(0);
    fetchDeals(0, nextPageSize, dealsStages);
  };

  const onDealsStagesChange = (nextStages: string[]) => {
    setDealsStages(nextStages);
    setDealsPage(0);
    fetchDeals(0, dealsPageSize, nextStages);
  };

  const fetchAllDealsForExport = useCallback(async (): Promise<DealSummary[]> => {
    if (recordType !== RecordType.DEALS) return [];
    if (!companyId) return [];
    if (!startDate || !endDate) return [];
    if (!startDate.isValid() || !endDate.isValid()) return [];
    if (startDate.isAfter(endDate, "day")) return [];

    const startIso = startDate.toISOString();
    const endIso = endDate.toISOString();
    const filterParams =
      dateType === DateType.CREATE
        ? { createDateFrom: startIso, createDateTo: endIso }
        : { closeDateFrom: startIso, closeDateTo: endIso };

    const pipelineLabel = pipelineId
      ? pipelines?.find((p) => p.sourcePipelineId === pipelineId)?.label
      : undefined;

    // pageSize is intentionally large so the export reflects every filtered
    // deal, not just the current page.
    const res = await getDeals({
      companyId,
      params: {
        paginationParams: { page: 0, pageSize: 10000 },
        filterParams: {
          ...filterParams,
          ...(pipelineLabel && { pipeline: pipelineLabel }),
          ...(dealsStages.length > 0 && { stages: dealsStages }),
        },
      },
    }).unwrap();

    return res.data ?? [];
  }, [
    recordType,
    companyId,
    startDate,
    endDate,
    dateType,
    pipelineId,
    pipelines,
    dealsStages,
    getDeals,
  ]);

  const onExportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const rows = await fetchAllDealsForExport();
      const csv = buildDealsCsv(rows);
      const stamp = dayjs().format("YYYY-MM-DD");
      triggerCsvDownload(csv, `filtered-deals-${stamp}.csv`);
    } catch (err) {
      console.error("CSV export failed", err);
      setExportError("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "#F2F2F2", width: "100vw", height: "100vh" }}>
      <TodaysBrief />
      <FilterBar
        recordType={recordType}
        dateType={dateType}
        dateRange={dateRange}
        startDate={startDate}
        endDate={endDate}
        pipelines={pipelines}
        pipelineId={pipelineId}
        onRecordTypeChange={setRecordType}
        onDateTypeChange={setDateType}
        onDateRangeChange={setDateRange}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onPipelineChange={setPipelineId}
        onApplyFilters={onApplyFilters}
        loading={loading}
      />
      {error && <p style={{ color: "red" }}>{error}</p>}

      <FunnelAnalytics stages={funnelStages} loading={loading} />
      <PerformanceMetrics
        recordType={recordType}
        stages={
          recordType === RecordType.DEALS ? dealPerformance : contactPerformance
        }
        loading={loading}
      />
      {recordType === RecordType.DEALS && (
        <DealsTable
          deals={deals}
          totalCount={dealsTotal}
          page={dealsPage}
          pageSize={dealsPageSize}
          loading={dealsLoading}
          stageOptions={
            // Pipeline stage labels match the deal row's `stage` field exactly
            // (both are resolved from pipeline.stages[].label on the backend) —
            // unlike funnelStages, whose labels can differ in case.
            pipelines
              ?.find((p) => p.sourcePipelineId === pipelineId)
              ?.stages.map((s) => s.label) ?? []
          }
          selectedStages={dealsStages}
          onStagesChange={onDealsStagesChange}
          onPageChange={onDealsPageChange}
          onPageSizeChange={onDealsPageSizeChange}
          onExportCsv={onExportCsv}
          exporting={exporting}
          exportError={exportError}
        />
      )}
    </Box>
  );
}
